import { Link } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { Avatar, AvatarFallback, AvatarImage, Icon, Text } from '@/_components';
import { CellText } from '@/_containers';
import type { SupportSource } from '@/_containers/support/shared/schemas/support-query-schema';
import { dateTime } from '@/_shared';

import { ticketTypeMapper } from './helper';

export function Header({
  imageUrl,
  name,
  email,
  date,
  className = '',
  link,
}: {
  imageUrl?: string;
  name: string;
  email: string;
  date?: string;
  className?: string;
  link?: string;
}) {
  return (
    <div className={twMerge('col-span-2 flex items-start gap-3', className)}>
      <Avatar>
        <AvatarImage src={imageUrl || undefined} />
        <AvatarFallback name={name || '***UNKNOWN***'} className="text-sm" />
      </Avatar>
      <div className="flex flex-col gap-px">
        {link ? (
          <Link className="decoration-primary hover:underline" to={link}>
            <CellText className="text-accent text-sm font-medium dark:text-white">{name || '***UNKNOWN***'}</CellText>
          </Link>
        ) : (
          <CellText className="text-accent text-sm font-medium dark:text-white">{name || '***UNKNOWN***'}</CellText>
        )}

        <CellText className="text-c-card-muted line-clamp-1 text-xs">{email || ''}</CellText>
        {date && (
          <div className="text-xs text-gray-500 italic">
            {dateTime.fromNow(date)} ({dateTime.format(date, 'ddd, MMMM DD, YYYY [at] hh:mm A')})
          </div>
        )}
      </div>
    </div>
  );
}

export function SubHeaderSection({
  bcc_emails,
  cc_emails,
  source,
  to_emails,
  isPrivate,
  isConversation,
  sentLabel = 'To',
}: {
  bcc_emails?: string[];
  cc_emails?: string[];
  source: SupportSource;
  to_emails?: string[];
  isPrivate?: boolean;
  isConversation?: boolean;
  sentLabel?: string;
}) {
  const sourceInfo = ticketTypeMapper(source);
  return (
    <section
      className={twMerge(
        'mb-2 flex items-start gap-3',
        ((bcc_emails?.length ?? 0 > 0) || (cc_emails?.length ?? 0 > 0)) && 'mb-6',
        isConversation && 'mb-4'
      )}
    >
      <div className="flex size-8 justify-center">
        <Icon
          icon={sourceInfo.icon}
          className={twMerge('text-accent size-4 dark:text-white', isPrivate && 'dark:text-accent')}
        />
      </div>
      <div className={isConversation ? 'flex justify-start gap-3' : 'space-y-px'}>
        {to_emails && (
          <Text as="p" className={twMerge('text-c-card-muted font-medium', isPrivate && 'dark:text-accent')}>
            <span className="font-semibold">{sentLabel}:</span> {to_emails.join(', ') || '***UNKNOWN***'}
          </Text>
        )}
        {(bcc_emails?.length ?? 0) > 0 && (
          <Text as="p" className={twMerge('text-c-card-muted text-xs', isPrivate && 'dark:text-accent')}>
            <span className="font-semibold">Bcc:</span> {bcc_emails?.join(', ')}
          </Text>
        )}
        {(cc_emails?.length ?? 0) > 0 && (
          <Text as="p" className={twMerge('text-c-card-muted text-xs', isPrivate && 'dark:text-accent')}>
            <span className="font-semibold">Cc:</span> {cc_emails?.join(', ')}
          </Text>
        )}
      </div>
    </section>
  );
}
